import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Plus, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import BlueCubeSpinner from "../components/BlueCubeSpinner";
import AdminSubNav from "../components/AdminSubNav";
import { isSuperadmin as checkSuperadmin } from "../lib/permissions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOrganizationsSummary = ({ user, setUser }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newOrgName, setNewOrgName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deletingOrg, setDeletingOrg] = useState(null);
  const navigate = useNavigate();

  const canManageOrgs =
    checkSuperadmin(user) ||
    (user?.organization || "").trim().toLowerCase() === "2ra-edition";

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API}/admin/users/organization-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Org summary error:", err);
      const msg = err.response?.data?.detail || "Error loading organization summary";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    const name = newOrgName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(
        `${API}/admin/organizations`,
        { organization: name },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Organization "${resp.data.organization}" created`);
      setNewOrgName("");
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create the organization");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteOrg = async (name) => {
    if (deletingOrg) return;
    if (!window.confirm(`Delete the empty organization "${name}"?`)) return;
    setDeletingOrg(name);
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/admin/organizations/${encodeURIComponent(name)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Organization "${name}" deleted`);
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete the organization");
    } finally {
      setDeletingOrg(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1
              className="text-xl font-semibold"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Admin Dashboard
            </h1>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                Public Site
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="organizations" />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-4">
          <h2
            className="text-2xl font-bold"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Organizations Summary
          </h2>
          <p className="text-xs text-slate-500 max-w-md">
            Shows the number of users per organization. Suspended users are included; users in the recycle bin are excluded.
          </p>
        </div>

        {canManageOrgs && (
          <form
            onSubmit={handleCreateOrg}
            className="mb-5 bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-end gap-3"
            data-testid="create-organization-form"
          >
            <div className="flex-1 min-w-[220px]">
              <label className="block text-sm font-medium mb-1">New organization</label>
              <Input
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="e.g. UNIKIN, MIT, UCL"
                maxLength={80}
                data-testid="new-organization-input"
              />
            </div>
            <Button
              type="submit"
              disabled={creating || !newOrgName.trim()}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="create-organization-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {creating ? "Creating…" : "Create organization"}
            </Button>
            <p className="w-full text-[11px] text-slate-400 m-0">
              Names are stored in UPPERCASE. New organizations appear immediately in the user-creation dropdown, even before they have users.
            </p>
          </form>
        )}

        {loading ? (
          <div className="flex items-center text-sm text-slate-600">
            Loading organization summary...
            <BlueCubeSpinner />
          </div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : data.length === 0 ? (
          <div className="text-sm text-slate-600">No organizations found.</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left font-medium text-slate-600 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-right font-medium text-slate-600 uppercase tracking-wider">
                    Users
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {data.map((row) => (
                  <tr key={row.organization}>
                    <td className="px-6 py-3 flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-800">
                        {(row.organization === "" || row.organization === "PublicPublic") ? "Public" : row.organization}
                      </span>
                      {row.count === 0 && (
                        <span className="rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold px-2 py-0.5 uppercase tracking-wide">
                          Empty
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <span className="font-mono text-slate-900">{row.count}</span>
                      {canManageOrgs && row.count === 0 && row.organization.toLowerCase() !== "public" && (
                        <button
                          type="button"
                          onClick={() => handleDeleteOrg(row.organization)}
                          disabled={deletingOrg === row.organization}
                          className="ml-4 inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-800 disabled:opacity-50"
                          data-testid={`delete-organization-${row.organization}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          {deletingOrg === row.organization ? "Deleting…" : "Delete"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrganizationsSummary;
